import { Suspense, useMemo } from "react"
import { Masonry } from "@mui/lab"
import { Grid, useMediaQuery, useTheme } from "@mui/material"
import { MerchantNewsCard } from "../components/MerchantNewsCard"
import { AddMerchantNewsCardButton } from "../components/AddMerchantNewsCardButton"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { FetchMerchantAdminNewsResponse, MerchantAdminNewsQuery, fetchMerchantAdminNews } from "../merchantAdminNewsApi"
import { NoNewsPlaceholder } from "../components/NoNewsPlaceholder"
import { MERCHANT_NEWS_CREATE_NEWS_ITEM_ROUTE } from "../../../../../constants/routes"
import { SkeletonCard } from "../components/SkeletonElements"
import { QueryErrorBoundary } from "../../../../../core/api/components/QueryErrorBoundary"
import { useMerchant } from "../../../../merchants/merchantHooks"

type MerchantNewsContentProps = Readonly<{
    columns: number
    spacing: number
}>

const selectNews = (data: FetchMerchantAdminNewsResponse) => data?.news || []

export const MerchantNewsContent = ({ columns, spacing }: MerchantNewsContentProps) => {
    const { merchantId } = useMerchant()

    const queryResult = useApiQuery(fetchMerchantAdminNews, {
        queryName: MerchantAdminNewsQuery.LIST,
        dependencies: {
            merchantId,
        },
        select: selectNews,
        suspense: true,
        useErrorBoundary: true,
    })

    const news = queryResult.data

    return (
        <Grid container width="100%">
            {news && news.length === 0 && (
                <NoNewsPlaceholder
                    header="news-no-news-title"
                    body="news-no-news-body"
                    linkText="news-no-news-here-link"
                    link={MERCHANT_NEWS_CREATE_NEWS_ITEM_ROUTE}
                />
            )}

            {news && (
                <Masonry columns={columns} spacing={spacing}>
                    {news.length > 0 && <AddMerchantNewsCardButton />}
                    {news.map((item) => {
                        return <MerchantNewsCard article={item} key={item.id} />
                    })}
                </Masonry>
            )}
        </Grid>
    )
}

export const MerchantNewsTab = () => {
    const theme = useTheme()
    const matches1000 = useMediaQuery(theme.breakpoints.down(1000))
    const matchesSmallScreen = useMediaQuery(theme.breakpoints.down("sm"))

    const setColumns = () => {
        if (matchesSmallScreen) {
            return 1
        }
        if (matches1000) {
            return 2
        }

        return 3
    }

    const setSpacing = () => {
        if (matches1000) {
            return 3
        }

        return 4
    }
    const columns = useMemo(() => setColumns(), [matchesSmallScreen, matches1000])
    const spacing = useMemo(() => setSpacing(), [matches1000])

    return (
        <>
            <QueryErrorBoundary>
                <Suspense
                    fallback={
                        <Masonry sx={{ margin: 0, alignItems: "center" }} columns={columns} spacing={spacing}>
                            {Array.from({ length: 6 }).map((_, index) => (
                                <SkeletonCard key={index} />
                            ))}
                        </Masonry>
                    }
                >
                    <MerchantNewsContent columns={columns} spacing={spacing} />
                </Suspense>
            </QueryErrorBoundary>
        </>
    )
}
